import { FormEvent, useEffect, useState } from 'react';
import { CategoriesApi, SuppliesApi, UnitsApi } from '../api/resources';
import { ApiError, apiDownloadUrl } from '../api/client';
import type { Category, Supply, Unit } from '../api/types';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { formatCOP, formatQty } from '../utils/format';

const emptyForm = {
  id: '',
  code: '',
  name: '',
  categoryId: '',
  unitId: '',
  unitCost: '',
  supplier: '',
  minStock: '',
};

export function Supplies() {
  const { notify } = useToast();
  const { user } = useAuth();
  const [items, setItems] = useState<Supply[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    CategoriesApi.list().then((r) => setCategories(r.categories));
    UnitsApi.list().then((r) => setUnits(r.units));
  }, []);

  async function load() {
    setLoading(true);
    try {
      const result = await SuppliesApi.list({
        search: search || undefined,
        categoryId: categoryId || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
        lowStock: lowStockOnly || undefined,
        page,
        pageSize: 15,
      });
      setItems(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar los insumos.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, categoryId, statusFilter, lowStockOnly, page]);

  function openCreate() {
    setForm(emptyForm);
    setFieldErrors({});
    setShowForm(true);
  }

  function openEdit(s: Supply) {
    setForm({
      id: s.id,
      code: s.code,
      name: s.name,
      categoryId: s.categoryId,
      unitId: s.unitId,
      unitCost: s.unitCost,
      supplier: s.supplier ?? '',
      minStock: s.minStock,
    });
    setFieldErrors({});
    setShowForm(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFieldErrors({});
    try {
      const payload = {
        code: form.code,
        name: form.name,
        categoryId: form.categoryId,
        unitId: form.unitId,
        unitCost: Number(form.unitCost),
        supplier: form.supplier || undefined,
        minStock: Number(form.minStock || 0),
      };
      if (form.id) {
        await SuppliesApi.update(form.id, payload);
        notify('Insumo actualizado correctamente.', 'success');
      } else {
        await SuppliesApi.create(payload);
        notify('Insumo creado con stock en cero. Registra las existencias como una Entrada en Inventario.', 'success');
      }
      setShowForm(false);
      load();
    } catch (err) {
      if (err instanceof ApiError) {
        notify(err.message, 'error');
        if (err.fieldErrors) setFieldErrors(err.fieldErrors);
      } else {
        notify('No fue posible guardar el insumo.', 'error');
      }
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(s: Supply) {
    try {
      if (s.isActive) {
        if (!window.confirm(`¿Desactivar el insumo "${s.name}"? Podras reactivarlo mas adelante.`)) return;
        await SuppliesApi.deactivate(s.id);
        notify('Insumo desactivado.', 'success');
      } else {
        await SuppliesApi.activate(s.id);
        notify('Insumo activado.', 'success');
      }
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible actualizar el estado.', 'error');
    }
  }

  return (
    <div>
      <div className="flex-between">
        <h1>Insumos</h1>
        <div className="actions-row">
          {user?.role === 'ADMIN' && (
            <a className="btn btn-secondary" href={apiDownloadUrl('/api/csv/supplies')}>
              Exportar insumos CSV
            </a>
          )}
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + Nuevo insumo
          </button>
        </div>
      </div>

      <div className="card">
        <div className="form-grid" style={{ marginBottom: 0 }}>
          <div className="field">
            <label htmlFor="search">Buscar</label>
            <input
              id="search"
              type="search"
              placeholder="Nombre o codigo..."
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="categoryFilter">Categoria</label>
            <select
              id="categoryFilter"
              value={categoryId}
              onChange={(e) => {
                setPage(1);
                setCategoryId(e.target.value);
              }}
            >
              <option value="">Todas</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="statusFilter">Estado</label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value as never);
              }}
            >
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
              <option value="all">Todos</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="lowStock">Filtro de stock</label>
            <div className="checkbox-field">
              <input
                id="lowStock"
                type="checkbox"
                checked={lowStockOnly}
                onChange={(e) => {
                  setPage(1);
                  setLowStockOnly(e.target.checked);
                }}
              />
              <label htmlFor="lowStock">Solo stock bajo</label>
            </div>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="card">
          <h2>{form.id ? 'Editar insumo' : 'Nuevo insumo'}</h2>
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="code">Codigo</label>
                <input
                  id="code"
                  required
                  disabled={!!form.id}
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
                {fieldErrors.code && <span className="field-error">{fieldErrors.code[0]}</span>}
              </div>
              <div className="field">
                <label htmlFor="name">Nombre</label>
                <input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                {fieldErrors.name && <span className="field-error">{fieldErrors.name[0]}</span>}
              </div>
              <div className="field">
                <label htmlFor="category">Categoria</label>
                <select id="category" required value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                  <option value="">Selecciona...</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="unit">Unidad de medida</label>
                <select id="unit" required value={form.unitId} onChange={(e) => setForm({ ...form, unitId: e.target.value })}>
                  <option value="">Selecciona...</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="unitCost">Costo unitario (COP)</label>
                <input
                  id="unitCost"
                  type="number"
                  min={0}
                  step="0.01"
                  required
                  value={form.unitCost}
                  onChange={(e) => setForm({ ...form, unitCost: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor="supplier">Proveedor</label>
                <input id="supplier" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="minStock">Stock minimo</label>
                <input
                  id="minStock"
                  type="number"
                  min={0}
                  step="0.001"
                  required
                  value={form.minStock}
                  onChange={(e) => setForm({ ...form, minStock: e.target.value })}
                />
              </div>
            </div>
            {!form.id && (
              <p className="hint">El insumo se crea con stock en cero. Registra las existencias iniciales como una Entrada en el modulo de Inventario.</p>
            )}
            <div className="actions-row">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading-block">Cargando insumos...</div>
        ) : items.length === 0 ? (
          <p className="empty-state">No se encontraron insumos con los filtros aplicados.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Nombre</th>
                  <th>Categoria</th>
                  <th className="text-right">Costo unitario</th>
                  <th>Proveedor</th>
                  <th className="text-right">Stock</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id}>
                    <td>{s.code}</td>
                    <td>{s.name}</td>
                    <td>{s.category?.name}</td>
                    <td className="text-right">{formatCOP(s.unitCost)}</td>
                    <td>{s.supplier || '-'}</td>
                    <td className={`text-right ${Number(s.currentStock) <= Number(s.minStock) ? 'text-danger' : ''}`}>
                      {formatQty(s.currentStock)} {s.unit?.name}
                    </td>
                    <td>
                      <span className={`pill ${s.isActive ? 'pill-active' : 'pill-inactive'}`}>{s.isActive ? 'Activo' : 'Inactivo'}</span>
                    </td>
                    <td>
                      <div className="actions-row">
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => openEdit(s)}>
                          Editar
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${s.isActive ? 'btn-danger' : 'btn-secondary'}`}
                          onClick={() => toggleActive(s)}
                        >
                          {s.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pagination">
          <span>
            {total} resultado(s) - pagina {page} de {totalPages}
          </span>
          <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}
